import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  PetAction,
  ActionCooldown,
  UserPet,
} from '@typings/pets/pet.interface';
import { ActionButton } from '../../components';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { useDocument } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';

type Props = {
  activePet?: UserPet | null;
};

export default function PetActionPanel({
  activePet,
}: Props) {
  const currentUid = useRecoilValue(currentUserIDState);
  const [actionCooldowns, setActionCooldowns] = useState<ActionCooldown[]>([]);
  // 액션 설정 불러오기 (PETSETTING/actions)
  const { data: actionsDoc } = useDocument<{ items: PetAction[] }>(
    `${CollectionPath.PETSETTING}/actions`,
    { listen: true },
  );

  const visibleActions = useMemo(() => {
    const enabledActions = (actionsDoc?.items || []).filter((action) => action.isEnabled);
    // 중복 id 제거 (첫 번째 항목만 유지)
    const uniqueActions = enabledActions.reduce((acc, action) => {
      if (!acc.some((a) => a.id === action.id)) {
        acc.push(action);
      }
      return acc;
    }, [] as PetAction[]);
    return uniqueActions;
  }, [actionsDoc?.items]);

  const columnCount = Math.max(1, Math.min(visibleActions.length, 4));

  // 초기 쿨다운 복원 (localStorage)
  useEffect(() => {
    if (!setActionCooldowns) return;
    try {
      const key = `pet_cooldowns_${currentUid || 'guest'}`;
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const saved = JSON.parse(raw) as Record<
        string,
        { lastUsed: string | Date; cooldownMinutes: number }
      >;
      const restored: ActionCooldown[] = Object.entries(saved).map(
        ([actionId, v]) => ({
          actionId,
          lastUsed: new Date(v.lastUsed),
          cooldownMinutes: v.cooldownMinutes,
        }),
      );
      setActionCooldowns(() => restored);
    } catch (e) {
      console.warn('쿨다운 복원 실패', e);
    }
  }, [currentUid]);

  return (
    <Grid $columns={columnCount}>
      {visibleActions.map((action) => (
        <ActionButton
          key={action.id}
          action={action}
          cooldown={actionCooldowns.find((cd) => cd.actionId === action.id)}
          activePet={activePet as any}
          currentUid={currentUid as any}
          setActionCooldowns={setActionCooldowns as any}
        />
      ))}
    </Grid>
  );
}

const Grid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => `repeat(${$columns}, 1fr)`};
  gap: 8px;
`;
